import { useCallback, type FC } from 'react';
import { Button, type ButtonVariant } from '~/components/ui/Button';
import { Link } from '~/components/ui/Link';
import { classNames } from '~/utils/classNames';

export type NavigationItem =
  | {
      type: 'link';
      label: string;
      href: string;
      target?: '_blank';
    }
  | {
      type: 'component';
      component: FC<{ className?: string }>;
      desktopOnly?: boolean;
      mobileOnly?: boolean;
    }
  | {
      type: 'button';
      label: string;
      onClick: VoidFunction;
      variant?: ButtonVariant;
    };

interface NavigationItemProps {
  item: NavigationItem;
  className?: string;
  onItemClick?: () => void;
}

export const NavigationItem = ({ item, className, onItemClick }: NavigationItemProps) => {
  const handleClick = useCallback(() => {
    if (item.type === 'button') {
      item.onClick();
    }

    onItemClick?.();
  }, [item, onItemClick]);

  if (item.type === 'button') {
    return (
      <Button variant={item.variant || 'link-gray'} onClick={handleClick} className={className}>
        {item.label}
      </Button>
    );
  }

  if (item.type === 'component') {
    const Component = item.component;

    return (
      <Component
        className={classNames(className, { 'hidden lg:flex': item.desktopOnly, 'lg:hidden flex': item.mobileOnly })}
      />
    );
  }

  if (item.href?.startsWith('http')) {
    return (
      <Link href={item.href} target={item.target} className={className} onClick={handleClick}>
        {item.label}
      </Link>
    );
  }

  return (
    <a href={item.href} target={item.target} className={className} onClick={handleClick}>
      {item.label}
    </a>
  );
};
